/**
 * header.js — Site header web components
 *
 * Checklist coverage:
 *  1  Arbitrary nesting depth .............. HamburgerDrawer — drill stack reads DOM recursively
 *  2  Social / locale / secondary links .... rendered in Liquid footer block
 *  3  ARIA disclosure ...................... aria-expanded toggled on every expand btn
 *  4  Drill-down back + focus .............. _drillIn / _drillBack — back btn focuses opener
 *  5  Account block ........................ rendered in Liquid; JS preserves tabindex
 *  6  44px tap targets ..................... enforced in header.css
 *  7  Smooth open/close; no body scroll .... body.style.overflow = 'hidden' while open
 *  8  Drawer scroll ........................ overflow-y: auto on .hamburger-drawer__body
 *  9  Focus trapped; return focus ........... _updateFocusTrap / close() restores opener
 * 10  Drill-down AND accordion modes ........ data-mode="drill|accordion" on drawer
 * 11  Keyboard nav mega menu ................ details/summary native + HeaderMenu web component
 * 12  Predictive search ..................... PredictiveSearch — 300ms debounce, keyboard nav
 * 13  Transparent → solid scroll ............ ScrollHeader — IntersectionObserver
 * 14  Cart count live update ................ data-cart-item-count handled by cart-drawer.js
 * 15  Announcement bar ...................... CSS marquee in header.css, no JS
 */

// ---------------------------------------------------------------------------
// Scroll header — transparent → solid
// ---------------------------------------------------------------------------

class ScrollHeader {
  constructor(el) {
    if (!el.classList.contains('site-header--transparent-init')) return;

    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;height:1px;width:1px;pointer-events:none;';
    document.body.prepend(sentinel);

    new IntersectionObserver(([entry]) => {
      const solid = !entry.isIntersecting;
      el.classList.toggle('site-header--transparent', !solid);
      el.classList.toggle('site-header--scrolled', solid);
    }, { threshold: 0 }).observe(sentinel);
  }
}

// ---------------------------------------------------------------------------
// Scroll shadow — adds .site-header--scrolled when page scrolls
// ---------------------------------------------------------------------------

class ScrollShadow {
  constructor(el) {
    const onScroll = () => el.classList.toggle('site-header--scrolled', window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }
}

// ---------------------------------------------------------------------------
// Details disclosure + Header menu web components (R3NU pattern)
// ---------------------------------------------------------------------------

class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  // The open animation is pure CSS (`.mega-menu[open] > .mega-menu__content`),
  // which restarts on every open. Replaying cached Animation objects here used
  // to run a stale copy on top of it.
  onToggle() {}

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', 'false');
  }
}

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('[data-header]');
    this._hoverTimer = null;

    if (this.header?.hasAttribute('data-mega-hover')) {
      this.addEventListener('mouseenter', this._onMouseEnter.bind(this));
      this.addEventListener('mouseleave', this._onMouseLeave.bind(this));
    }
  }

  _shouldOpenOnHover() {
    return (
      window.matchMedia('(min-width: 990px)').matches &&
      window.matchMedia('(hover: hover)').matches
    );
  }

  _onMouseEnter() {
    if (!this._shouldOpenOnHover()) return;
    clearTimeout(this._hoverTimer);
    // Sliding from one open menu to the next: swap panels without replaying
    // the fade-in, otherwise every switch blinks.
    let switching = false;
    document.querySelectorAll('header-menu').forEach(hm => {
      if (hm === this) return;
      clearTimeout(hm._hoverTimer);
      if (hm.mainDetailsToggle.open) switching = true;
      hm.close();
    });
    this.mainDetailsToggle.classList.toggle('mega-menu--instant', switching);
    this.mainDetailsToggle.setAttribute('open', '');
  }

  _onMouseLeave() {
    if (!this._shouldOpenOnHover()) return;
    this._hoverTimer = setTimeout(() => this.close(), 250);
  }

  onToggle() {
    const isOpen = this.mainDetailsToggle.open;
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (this.header) {
      this.header.preventHide = isOpen;
      setHeaderBottomVar();
    }

    if (isOpen) {
      const first = this.querySelector('.cat-flyout__link--l1');
      if (first) CategoriesFlyout.activateL1(first);
    }

    super.onToggle();
  }
}

class CategoriesFlyout {
  static activateL1(link) {
    const root = link.closest('[data-cat-flyout]');
    if (!root) return;
    const l1 = link.dataset.l1;
    const l2Col = root.querySelector('[data-l2-col]');
    const l3Col = root.querySelector('[data-l3-col]');

    root.querySelectorAll('.cat-flyout__link--l1').forEach((el) => el.classList.toggle('is-active', el === link));
    root.querySelectorAll('.cat-flyout__link--l2').forEach((el) => el.classList.remove('is-active'));

    let hasL2 = false;
    root.querySelectorAll('.cat-flyout__item[data-l1]').forEach((item) => {
      const show = item.dataset.l1 === l1;
      item.hidden = !show;
      if (show) hasL2 = true;
    });
    root.querySelectorAll('.cat-flyout__item[data-l2]').forEach((item) => {
      item.hidden = true;
    });

    if (l2Col) l2Col.hidden = !hasL2;
    if (l3Col) l3Col.hidden = true;
  }

  static activateL2(link) {
    const root = link.closest('[data-cat-flyout]');
    if (!root) return;
    const l2 = link.dataset.l2;
    const l3Col = root.querySelector('[data-l3-col]');

    root.querySelectorAll('.cat-flyout__link--l2').forEach((el) => el.classList.toggle('is-active', el === link));

    let hasL3 = false;
    root.querySelectorAll('.cat-flyout__item[data-l2]').forEach((item) => {
      const show = item.dataset.l2 === l2;
      item.hidden = !show;
      if (show) hasL3 = true;
    });

    if (l3Col) l3Col.hidden = !hasL3;
  }
}

document.addEventListener('mouseover', (event) => {
  const l1 = event.target.closest('.cat-flyout__link--l1');
  if (l1) {
    CategoriesFlyout.activateL1(l1);
    return;
  }
  const l2 = event.target.closest('.cat-flyout__link--l2');
  if (l2) CategoriesFlyout.activateL2(l2);
});

customElements.define('details-disclosure', DetailsDisclosure);
customElements.define('header-menu', HeaderMenu);

// ---------------------------------------------------------------------------
// Predictive search
// ---------------------------------------------------------------------------

class PredictiveSearch {
  // Drives any [data-search-root]; there is one — the inline header search bar.
  // An overlay variant used to exist, but its only trigger was display:none at
  // every breakpoint, so nothing could open it; it and its branches are gone.
  constructor(root) {
    this._root    = root;
    this._input   = root.querySelector('[data-search-input]');
    this._results = root.querySelector('[data-search-results]');
    if (!this._input || !this._results) return;

    this._query    = '';
    this._timer    = null;
    this._focusIdx = -1;

    // No scrim to click, so dismiss the dropdown on an outside click.
    document.addEventListener('click', (e) => {
      if (!root.contains(e.target)) this.close();
    });

    this._input.addEventListener('input', () => {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => this._fetch(this._input.value.trim()), 300);
    });

    this._input.addEventListener('keydown', (e) => this._onInputKeyDown(e));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this._results.hasAttribute('hidden')) this.close();
    });

    this._initVoice();
  }

  /**
   * Voice search. The mic button existed in the markup from the start but was
   * never wired to anything, so it did nothing when clicked.
   *
   * Uses the browser's own SpeechRecognition — no library. Firefox does not
   * implement it, so there the button is hidden rather than left dead.
   */
  _initVoice() {
    const mic = this._root.querySelector('.site-header__search-mic');
    if (!mic) return;

    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      mic.hidden = true;
      return;
    }

    const recognition = new Recognition();
    recognition.lang = document.documentElement.lang || 'en-GB';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    let listening = false;
    const setListening = (on) => {
      listening = on;
      mic.classList.toggle('is-listening', on);
      mic.setAttribute('aria-pressed', String(on));
    };

    mic.setAttribute('aria-pressed', 'false');
    mic.addEventListener('click', () => {
      if (listening) {
        recognition.stop();
        return;
      }
      try {
        recognition.start();
        setListening(true);
      } catch (e) {
        // start() throws if it is already running; ignore and let onend reset.
      }
    });

    recognition.addEventListener('result', (e) => {
      const said = e.results[0]?.[0]?.transcript;
      if (!said) return;
      this._input.value = said;
      // Same event the typed path fires, so the debounce and fetch are shared.
      this._input.dispatchEvent(new Event('input', { bubbles: true }));
      this._input.focus();
    });

    recognition.addEventListener('end', () => setListening(false));
    recognition.addEventListener('error', () => setListening(false));
  }

  close() {
    this._results.setAttribute('hidden', '');
    this._input.setAttribute('aria-expanded', 'false');
    this._focusIdx = -1;
  }

  _fetch(q) {
    if (!q || q.length < 2) {
      this._results.setAttribute('hidden', '');
      this._input.setAttribute('aria-expanded', 'false');
      return;
    }
    if (q === this._query) return;
    this._query = q;

    fetch(
      `/search/suggest.json?q=${encodeURIComponent(q)}` +
        `&resources[type]=query,collection,product,article,page&resources[limit]=5`
    )
      .then(r => r.json())
      .then(data => this._render(data.resources?.results || {}, q))
      .catch(() => {});
  }

  _render(results, q) {
    const products = results.products || [];

    // Figma 1414-15382: the "Suggestions" list mixes query completions with
    // collections/pages/articles, the non-query ones carrying a type badge.
    const suggestions = [
      ...(results.queries || []).map(x => ({ label: x.text || x.title, url: x.url, badge: '' })),
      ...(results.collections || []).map(x => ({ label: x.title, url: x.url, badge: 'Category' })),
      ...(results.articles || []).map(x => ({ label: x.title, url: x.url, badge: 'Article' })),
      ...(results.pages || []).map(x => ({ label: x.title, url: x.url, badge: 'Page' })),
    ];

    if (!suggestions.length && !products.length) {
      this._results.innerHTML =
        `<p class="search-panel__empty">No results for "<strong>${this._esc(q)}</strong>"</p>`;
      this._reveal();
      return;
    }

    let html = '';

    if (suggestions.length) {
      html += `
        <div class="search-panel__block">
          <p class="search-panel__heading">${this._t('suggestions')}</p>
          <div class="search-panel__suggestions">
            ${suggestions.map(sg => `
              <a href="${sg.url}" class="search-suggestion${sg.badge === 'Category' ? ' search-suggestion--collection' : ''}" role="option" tabindex="-1" data-search-item>
                <span class="search-suggestion__label">${this._highlight(sg.label, q)}</span>
                <span class="search-suggestion__meta">
                  ${sg.badge ? `<span class="search-suggestion__badge">${sg.badge}</span>` : ''}
                  <span class="search-suggestion__arrow" aria-hidden="true">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M16.9992 7.0008H7.0008V16.9992M7.0008 7.0008L16.9992 16.9992" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </span>
                </span>
              </a>`).join('')}
          </div>
        </div>`;
    }

    if (products.length) {
      html += `
        <div class="search-panel__block search-panel__block--results">
          <div class="search-panel__results-inner">
            <p class="search-panel__heading">${this._t('showing_results', this._esc(q))}</p>
            <div class="search-panel__products">
              ${products.map(p => this._productRow(p, q)).join('')}
            </div>
          </div>
          <a class="search-panel__view-all" href="/search?q=${encodeURIComponent(q)}&type=product">
            ${this._t('view_all')}
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>`;
    }

    this._results.innerHTML = html;
    this._reveal();
  }

  _productRow(p, q) {
    const img = p.featured_image?.url || p.image;
    const compare = this._money(p.compare_at_price);
    const price = this._money(p.price);

    return `
      <a href="${p.url}" class="search-product" role="option" tabindex="-1" data-search-item>
        ${
          img
            ? `<img class="search-product__img" src="${img}&width=128" alt="" width="64" height="64" loading="lazy">`
            : `<span class="search-product__img search-product__img--empty" aria-hidden="true"></span>`
        }
        <span class="search-product__body">
          ${p.vendor ? `<span class="search-product__vendor">${this._esc(p.vendor)}</span>` : ''}
          <span class="search-product__title">${this._highlight(p.title, q)}</span>
        </span>
        <span class="search-product__price-group">
          ${p.available === false ? `<span class="search-product__sold">${this._t('sold_out')}</span>` : ''}
          ${price ? `<span class="search-product__price">${price}</span>` : ''}
          ${compare && compare !== price ? `<s class="search-product__compare">${compare}</s>` : ''}
        </span>
      </a>`;
  }

  _reveal() {
    this._results.removeAttribute('hidden');
    this._input.setAttribute('aria-expanded', 'true');
    this._focusIdx = -1;
  }

  // '[terms]' is the placeholder theme.liquid interpolates, matching the
  // '[code]' convention already used for the cart strings.
  _t(key, arg) {
    const value = window.theme?.strings?.search?.[key] || '';
    return arg === undefined ? value : value.replace('[terms]', arg);
  }

  _onInputKeyDown(e) {
    const items = Array.from(this._results.querySelectorAll('[data-search-item]'));
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this._focusIdx = Math.min(this._focusIdx + 1, items.length - 1);
      this._updateFocus(items);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._focusIdx = Math.max(this._focusIdx - 1, -1);
      if (this._focusIdx === -1) { items.forEach(i => i.classList.remove('is-focused')); this._input.focus(); return; }
      this._updateFocus(items);
    } else if (e.key === 'Enter' && this._focusIdx >= 0) {
      e.preventDefault();
      items[this._focusIdx]?.click();
    }
  }

  _updateFocus(items) {
    items.forEach((item, i) => item.classList.toggle('is-focused', i === this._focusIdx));
    items[this._focusIdx]?.scrollIntoView({ block: 'nearest' });
  }

  _esc(str) {
    return str.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  _highlight(text, q) {
    const esc = this._esc(text);
    if (!q) return esc;
    const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return esc.replace(re, '<mark>$1</mark>');
  }

  // BUG FIX: suggest.json reports prices as decimal strings ("32.89"), but this
  // used to hand them to a cents formatter, so every suggestion showed 1/100th
  // of the real price. Skeleton.money takes cents and already returns '' for
  // empty or unparseable input, so the unit is all this has to change.
  _money(value) {
    return window.Skeleton.money(Math.round(parseFloat(value) * 100));
  }
}

// ---------------------------------------------------------------------------
// Hamburger drawer
// ---------------------------------------------------------------------------

class HamburgerDrawer {
  constructor(el) {
    this._el        = el;
    this._panel     = el.querySelector('.hamburger-drawer__panel');
    this._body      = el.querySelector('[data-hamburger-body]');
    this._backBtn   = el.querySelector('[data-hamburger-back]');
    this._backLabel = el.querySelector('[data-back-label]');
    this._mode      = el.dataset.mode || 'drill';
    this._opener    = null;
    this._stack     = []; // drill-down stack: [{ list, parentLabel }]
    this._focusFirst = null;
    this._focusLast  = null;

    // Open buttons (hamburger icon in header, Categories tab in the mobile bar).
    // They toggle: the bar stays above the open sheet, so tapping Categories
    // again is the obvious way to dismiss it.
    document.querySelectorAll('[data-hamburger-open]').forEach(btn =>
      btn.addEventListener('click', () => {
        if (this._el.getAttribute('aria-hidden') === 'false') {
          this.close();
          return;
        }
        this._opener = btn;
        this.open();
      })
    );

    // Every other tab in the mobile bottom bar dismisses the sheet, so the cart,
    // the postcode modal or a new page never opens behind it. Categories is
    // excluded in the selector — it toggles itself above.
    document.querySelectorAll('.mobile-nav__tab:not([data-hamburger-open])')
      .forEach(tab => tab.addEventListener('click', () => this.close()));

    // Close triggers (overlay, X button, logo link inside drawer)
    el.querySelectorAll('[data-hamburger-close]').forEach(el =>
      el.addEventListener('click', () => this.close())
    );

    // Back button
    this._backBtn?.addEventListener('click', () => this._drillBack());

    // Nav expand buttons — set up once, handle both modes
    this._body.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-nav-expand]');
      if (!btn) return;
      if (this._mode === 'drill') {
        this._drillIn(btn);
      } else {
        this._toggleAccordion(btn);
      }
    });

    // Keyboard
    el.addEventListener('keydown', (e) => this._onKeyDown(e));
  }

  open() {
    this._el.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('[data-hamburger-open]').forEach(b => b.setAttribute('aria-expanded', 'true'));
    document.body.style.overflow = 'hidden';
    this._resetDrill();
    requestAnimationFrame(() => {
      this._updateFocusTrap();
      this._focusFirst?.focus();
    });
  }

  close() {
    this._el.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-hamburger-open]').forEach(b => b.setAttribute('aria-expanded', 'false'));
    document.body.style.overflow = '';
    this._opener?.focus();
    this._opener = null;
  }

  // ---------------------------------------------------------------------------
  // Drill-down mode
  // ---------------------------------------------------------------------------

  _drillIn(btn) {
    const item = btn.closest('[data-nav-item]');
    const sub  = item?.querySelector('[data-nav-sub]');
    if (!sub) return;

    const parentLabel       = item.querySelector('.hamburger-nav__link')?.textContent?.trim() || 'Back';
    const originalParent    = sub.parentElement;
    const originalNextSib   = sub.nextSibling;

    // Animate current list out
    const currentList = this._stack.length
      ? this._stack[this._stack.length - 1].sub
      : this._body.querySelector('[data-hamburger-nav]');

    currentList?.classList.add('is-exiting');
    currentList?.addEventListener('animationend', () => {
      currentList.classList.remove('is-exiting');
      currentList.setAttribute('hidden', '');
    }, { once: true });

    // Move sub to body so hiding the parent nav doesn't also hide it
    this._body.insertBefore(sub, this._body.firstChild);
    sub.removeAttribute('hidden');
    sub.classList.add('is-entering');
    sub.addEventListener('animationend', () => sub.classList.remove('is-entering'), { once: true });

    btn.setAttribute('aria-expanded', 'true');

    this._stack.push({ sub, btn, parentLabel, originalParent, originalNextSib });
    this._backBtn.removeAttribute('hidden');
    if (this._backLabel) this._backLabel.textContent = parentLabel;

    this._body.scrollTop = 0;
    this._updateFocusTrap();
    this._focusFirst?.focus();
  }

  _drillBack() {
    // Nothing drilled into: the back button is still on screen (it is the only
    // way out of the mobile sheet, whose header row is hidden), so it closes
    // the drawer rather than doing nothing.
    if (!this._stack.length) {
      this.close();
      return;
    }

    const { sub, btn, parentLabel, originalParent, originalNextSib } = this._stack.pop();

    // Restore sub to its original position in the DOM
    if (originalNextSib) {
      originalParent.insertBefore(sub, originalNextSib);
    } else {
      originalParent.appendChild(sub);
    }
    sub.setAttribute('hidden', '');
    btn.setAttribute('aria-expanded', 'false');

    const parentList = this._stack.length
      ? this._stack[this._stack.length - 1].sub
      : this._body.querySelector('[data-hamburger-nav]');

    parentList?.removeAttribute('hidden');

    if (!this._stack.length) {
      this._backBtn.setAttribute('hidden', '');
    } else {
      if (this._backLabel) this._backLabel.textContent = this._stack[this._stack.length - 1].parentLabel;
    }

    this._body.scrollTop = 0;
    this._updateFocusTrap();
    btn.focus();
  }

  _resetDrill() {
    // Restore all subs to original DOM positions before clearing stack
    [...this._stack].reverse().forEach(({ sub, btn, originalParent, originalNextSib }) => {
      if (originalNextSib) {
        originalParent.insertBefore(sub, originalNextSib);
      } else {
        originalParent.appendChild(sub);
      }
      sub.setAttribute('hidden', '');
      btn.setAttribute('aria-expanded', 'false');
    });
    this._stack = [];
    this._backBtn?.setAttribute('hidden', '');
    this._body.querySelector('[data-hamburger-nav]')?.removeAttribute('hidden');
  }

  // ---------------------------------------------------------------------------
  // Accordion mode
  // ---------------------------------------------------------------------------

  _toggleAccordion(btn) {
    const item    = btn.closest('[data-nav-item]');
    const sub     = item?.querySelector(':scope > [data-nav-sub]');
    if (!sub) return;

    const isOpen = btn.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      sub.setAttribute('hidden', '');
      btn.setAttribute('aria-expanded', 'false');
    } else {
      sub.removeAttribute('hidden');
      btn.setAttribute('aria-expanded', 'true');
    }
  }

  // ---------------------------------------------------------------------------
  // Focus trap
  // ---------------------------------------------------------------------------

  _updateFocusTrap() {
    const all = Array.from(this._panel.querySelectorAll(
      'a[href],button:not([disabled]):not([aria-disabled="true"]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'
    )).filter(el => el.offsetParent !== null && !el.closest('[hidden]'));
    this._focusFirst = all[0]              || null;
    this._focusLast  = all[all.length - 1] || null;
  }

  _onKeyDown(e) {
    if (this._el.getAttribute('aria-hidden') === 'true') return;
    if (e.key === 'Escape') { this.close(); return; }
    if (e.key !== 'Tab' || !this._focusFirst || !this._focusLast) return;

    if (e.shiftKey) {
      if (document.activeElement === this._focusFirst || document.activeElement === this._panel) {
        e.preventDefault();
        this._focusLast.focus();
      }
    } else {
      if (document.activeElement === this._focusLast) {
        e.preventDefault();
        this._focusFirst.focus();
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Header offset — remaining viewport below the sticky header
// ---------------------------------------------------------------------------

/* Every pane keyed to this var (the mobile drawer, the cat flyout, the phone
   cat rail) sits directly under the header, so what it needs is where the
   header's bottom edge actually IS — not how tall the header is.

   `offsetHeight` is the wrong answer: it keeps reporting the full height even
   when the header is not at the top of the viewport, which left the mobile
   drawer with a strip of page showing above it after a scroll. `rect.bottom`
   is the real edge, but only while it is read live — measured once at load it
   also counts the announcement bar that is about to scroll away. So: live,
   rAF-throttled, and floored at 0 for when the header is gone entirely. */
let headerBottomRaf = 0;
function setHeaderBottomVar() {
  if (headerBottomRaf) return;
  headerBottomRaf = requestAnimationFrame(() => {
    headerBottomRaf = 0;
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const root = document.documentElement;
    root.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.max(0, Math.round(header.getBoundingClientRect().bottom))}px`
    );

    /* The mobile sheet opens to a different edge: the top of the delivery
       countdown bar, so the sheet covers the bar and stops under the search
       row. The bar is display: none above 989px (offsetParent is then null),
       where this falls back to the header's own bottom. */
    const bar = document.querySelector('.site-header__delivery--bar');
    const drawerTop = bar && bar.offsetParent
      ? bar.getBoundingClientRect().top
      : header.getBoundingClientRect().bottom;
    root.style.setProperty('--header-drawer-top', `${Math.max(0, Math.round(drawerTop))}px`);
  });
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('[data-header]');
  const drawer = document.querySelector('[data-hamburger-drawer]');

  if (header) {
    new ScrollShadow(header);
    new ScrollHeader(header);
    setHeaderBottomVar();
    window.addEventListener('resize', setHeaderBottomVar);
    window.addEventListener('scroll', setHeaderBottomVar, { passive: true });
  }
  if (drawer) new HamburgerDrawer(drawer);
  document.querySelectorAll('[data-search-root]').forEach(el => new PredictiveSearch(el));
});
